import type { KioskAssetItem, KioskImageOption, KioskLayoutOption } from '../types/kiosk-flow-types';

// XML S3 path: .../{operatorId}[/{machineId}]/xml/{size}/{file}
const XML_PATH = /\/([^/]+)\/xml\/([^/]+)\/[^/]+$/;
// Image S3 path: .../{operatorId}[/{machineId}]/images/{file}
const IMAGE_PATH = /\/([^/]+)\/images\/([^/]+)$/;
const SIZE_ORDER = ['mdpi', 'hdpi', 'xhdpi'];

function sizeRank(size: string): number {
  const index = SIZE_ORDER.indexOf(size);
  return index === -1 ? SIZE_ORDER.length : index;
}

// S3 URL → its path without host, query or fragment
function toPath(url: string | null | undefined): string {
  let path = url ?? '';
  try {
    path = new URL(path).pathname;
  } catch {
    // Not an absolute URL — match against the raw value
  }
  return path.split(/[?#]/)[0];
}

function decode(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

// Asset Manager XMLs → one option per layout name with the sizes it exists in.
// Only operator-level files (/{operatorId}/xml/{size}/{file}) are kept — machine-level
// files and URLs in any other shape are left out.
export function groupLayouts(assets: KioskAssetItem[], operatorId: number): KioskLayoutOption[] {
  const byName = new Map<string, Set<string>>();
  for (const asset of assets) {
    const layoutName = (asset.currentFileName ?? '').replace(/\.xml$/i, '').trim();
    if (!layoutName) continue;

    const match = XML_PATH.exec(toPath(asset.currentS3Url));
    if (!match || match[1] !== String(operatorId)) continue;

    const sizes = byName.get(layoutName) ?? new Set<string>();
    sizes.add(match[2]);
    byName.set(layoutName, sizes);
  }
  return [...byName.entries()]
    .map(([layoutName, sizes]) => ({ layoutName, sizes: [...sizes].sort((a, b) => sizeRank(a) - sizeRank(b)) }))
    .sort((a, b) => a.layoutName.localeCompare(b.layoutName));
}

// Asset Manager images → one option per file in the operator-level images folder (/{operatorId}/images/{file}),
// sorted by title. The kiosk loads the banner from that folder by file name, so machine-level images are
// left out. The file name comes from the S3 path — the same thing the backoffice checks on Publish; the
// title is the Asset Manager name, or the file name when the asset has none.
export function listImages(assets: KioskAssetItem[], operatorId: number): KioskImageOption[] {
  const byFileName = new Map<string, string>();
  for (const asset of assets) {
    const match = IMAGE_PATH.exec(toPath(asset.currentS3Url));
    if (!match || match[1] !== String(operatorId)) continue;
    const fileName = decode(match[2]).trim();
    if (!fileName) continue;
    const title = (asset.name ?? '').trim();
    // The first titled entry wins for a file listed twice
    if (!byFileName.get(fileName)) byFileName.set(fileName, title);
  }
  return [...byFileName.entries()]
    .map(([fileName, title]) => ({ fileName, title: title || fileName }))
    .sort((a, b) => a.title.localeCompare(b.title) || a.fileName.localeCompare(b.fileName));
}
